import { zodResolver } from "@hookform/resolvers/zod"
import type React from "react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { Link, NavLink, useNavigate } from "react-router-dom"
import styled from "styled-components"
import { z } from "zod"
import backgroundImage from "~/assets/arbeidsark-bg-illustration.svg"
import { Button, FormField, Label } from "~/components"
import { ControlledInput } from "~/components/HookForm/ControlledInput"
import { FullPageLoader } from "~/components/Loader"
import Toast from "~/modules/toasts/Toast"
import { Margin } from "~/ur-react-components"
import { useTranslate } from "~/ur-react-hooks"
import { trpc } from "~/utils/trpc"

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;

  background-image: url(${backgroundImage});

  ${(props) =>
    props.theme.media.mobile &&
    `
      width: 100%;
  `}
`

const Title = styled.h3`
  font-family: 'Work Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 145%;

  margin-top: 1rem;
  margin-bottom: 1rem;
`

const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: 394px;
  box-sizing: border-box;

  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 8px 32px 0 rgba(27, 12, 55, 0.12);
`

/*
const FeideButton = styled.a`
  display: flex;
  appearance: none;
  border: none;
  background-color: #1f4698;
  color: white;
  height: 40px;
  justify-content: center;
  align-items: center;

  margin-bottom: 2rem;
  cursor: pointer;
`
*/

const BottomSection = styled.div`
  text-align: center;
  font-size: 14px;
  margin-top: 2rem;
  margin-bottom: 1rem;
`

const RegisterUserText = styled.p`
  color: ${(props) => props.theme.colors.gray0};
  margin: 0.5rem;
`

const RegisterUserLink = styled(Link)`
  display: block;
  color: ${(props) => props.theme.colors.primary};
  font-weight: bold;
`

const _ForgottenPasswordLink = styled(Link)`
  margin-top: 2rem;
  display: block;
  color: ${(props) => props.theme.colors.gray50};
  text-decoration-line: underline;
`

type LoginProps = {}

const Schema = z.object({
  email: z.string().email(),
})

type Schema = z.infer<typeof Schema>

export const Login: React.FC<LoginProps> = () => {
  const [showConfirmation, setShowConfirmation] = useState(false)

  const translations = useTranslate({
    login: "common.log-in",
    email: "common.email",
    password: "common.password",
    notAlreadyRegistered: "login.not-already-registered",
    createUser: "login.create-user",
    forgottenPassword: "login.forgotten-password",
    error: {
      createUser: "login.create-user-error",
      emailTaken: "login.email-address-already-in-use",
      wrongUsernameOrPassword: "error.wrong-username-or-password",
      badEmail: "errors.invalid-email-address",
      generic: "errors.generic-error",
    },
  })

  const nav = useNavigate()

  const { register, handleSubmit, reset, watch } = useForm<Schema>({
    defaultValues: {
      email: "",
    },
    resolver: zodResolver(Schema),
  })

  const { data: isLoggedIn, isLoading: isLoggedInLoading } =
    trpc.authentication.isLoggedIn.useQuery()

  const {
    mutate: loginViaEmail,
    error: loginEmailError,
    isPending: loginEmailLoading,
  } = trpc.authentication.loginViaEmail.useMutation({
    onSuccess() {
      setShowConfirmation(true)
    },
  })

  const values = watch()

  const submitDisabled = useMemo(
    () =>
      loginEmailLoading ||
      !values.email ||
      !z.string().email().safeParse(values.email).success,
    [loginEmailLoading, values.email],
  )

  const onSubmit = useCallback(
    (data: Schema) => {
      if (submitDisabled) {
        return
      }

      loginViaEmail({ email: data.email })
    },
    [loginViaEmail, submitDisabled],
  )

  useEffect(() => {
    if (isLoggedIn) nav("/")
  }, [isLoggedIn, nav])

  if (isLoggedInLoading) return <FullPageLoader />

  if (showConfirmation) {
    return (
      <Wrapper>
        <Card>
          <Title>Sjekk e-posten din</Title>
          <p className={"leading-normal"}>
            Vi har sendt en lenke til{" "}
            <span className={"font-medium text-gray-700"}>
              {values.email || "tormod@example.com"}
            </span>{" "}
            som du kan bruke til å logge inn med.
          </p>
          <p className={"mt-4"}>
            {" "}
            Følg instruksjonene i e-posten for å logge inn.
          </p>

          <div className="text-center">
            <button
              aria-label={"Try again"}
              className={"mt-8 text-gray-600 underline"}
              onClick={() => {
                setShowConfirmation(false)
                reset()
              }}
            >
              Tilbake til innlogging
            </button>
          </div>
        </Card>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <Card>
        <Title>{translations.login}</Title>

        {/*
        <FeideButton
          href={`https://auth.dataporten.no/oauth/authorization?client_id=${
            import.meta.env.VITE_APP_FEIDE_CLIENT_ID
          }&redirect_uri=${encodeURIComponent(
            import.meta.env.VITE_APP_FEIDE_REDIRECT_URI
          )}&response_type=code&scope=openid`}
        >
          Logg inn med Feide
        </FeideButton>
        */}

        {loginEmailError && (
          <div className={"mb-4"}>
            <Toast type="error" message={translations.error.generic} />
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <FormField margin="1rem">
            <Label>{translations.email}</Label>
            <ControlledInput register={register} name={"email"} />
          </FormField>
          <Margin top="1.5rem">
            <Button disabled={submitDisabled} width="100%" type={"submit"}>
              {translations.login}
            </Button>
          </Margin>
        </form>
        <BottomSection>
          <RegisterUserText>
            {translations.notAlreadyRegistered}
          </RegisterUserText>
          <RegisterUserLink to="/register">
            {translations.createUser}
          </RegisterUserLink>
          <NavLink to={"/login-with-password"}>
            <div className="pointer mt-8 text-gray-500 text-xs">
              Logg inn med brukernavn og passord
            </div>
          </NavLink>
        </BottomSection>
      </Card>
    </Wrapper>
  )
}
